"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePastExamCompletion } from "@/hooks/usePastExamCompletion";
import RoundCompletionBadge from "@/components/past-exam/RoundCompletionBadge";

const FALLBACK_IMAGE = "/past-exam/past-exam-clock_gamja.webp";

interface PastExamIconData {
  href: string;
  label: string;
  priority: string;
  difficulty?: string;
  description?: string;
}

interface PastExamIconGridProps {
  exams: PastExamIconData[];
  columns?: 3 | 4;
}

export default function PastExamIconGrid({
  exams,
  columns = 3,
}: PastExamIconGridProps) {
  // 모바일은 항상 3열(좁은 폰에서 4열이면 아이콘이 과하게 작아지고 라벨이 끼임 +
  // 아래 Image sizes 의 33vw 힌트와도 일치). columns=4 는 sm 이상에서만 4열로 확장.
  const gridCols = columns === 4 ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-3";
  // 기출 회차 완주 뱃지(프리미엄 이상). FolderGrid 와 동일 데이터/표시. JCG-2242
  const { showCompletion, completionForHref } = usePastExamCompletion();
  return (
    <div className={`grid ${gridCols} gap-3 sm:gap-4 mt-2`}>
      {exams.map((exam) => (
        <PastExamIcon
          key={exam.href}
          exam={exam}
          showCompletion={showCompletion}
          completionCount={completionForHref(exam.href)}
        />
      ))}
    </div>
  );
}

function PastExamIcon({
  exam,
  showCompletion,
  completionCount,
}: {
  exam: PastExamIconData;
  showCompletion: boolean;
  completionCount: number;
}) {
  const initialSrc = `/folder-icons${exam.href}.webp`;
  const [imgSrc, setImgSrc] = useState(initialSrc);

  return (
    <Link
      href={exam.href}
      prefetch={false}
      className="group flex flex-col items-center rounded-[22%] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
    >
      <div className="relative w-full aspect-square">
        <div className="absolute inset-0 rounded-[22%] border overflow-hidden transition-all duration-200 group-hover:shadow-md group-hover:scale-[1.04] group-hover:border-muted-foreground/30 group-focus-visible:border-amber-500">
          <Image
            src={imgSrc}
            alt={exam.label}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 33vw, 20vw"
            onError={() => setImgSrc(FALLBACK_IMAGE)}
          />
        </div>

        {/* 우측 상단 클러스터: 중요도 + 완주 뱃지(🏆 N회 / 미완주 빨간 !). JCG-2242 */}
        <div className="absolute -top-1 -right-1 flex items-center justify-end gap-0.5 z-10 max-w-[calc(100%+0.5rem)]">
          {exam.priority && (
            <div className="min-w-5 h-5 px-1 bg-background/90 dark:bg-background/80 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background text-sm leading-none">
              <span className="select-none">{exam.priority}</span>
            </div>
          )}
          {showCompletion && (
            <RoundCompletionBadge completionCount={completionCount} />
          )}
        </div>
      </div>
      <span className="mt-1.5 text-sm sm:text-base text-center font-medium leading-tight w-full">
        {exam.label}
      </span>
      {exam.description && (
        <span className="text-[11px] sm:text-xs text-center line-clamp-2 w-full leading-tight text-muted-foreground group-hover:text-foreground transition-colors">
          {exam.description}
        </span>
      )}
    </Link>
  );
}
